import { Box, Divider, styled } from "@mui/material";
import { tokens } from "@styles/tokens";
import authBg from "@assets/images/auth-bg.webp";
import { Testimonial } from "./Testimonial";
import { Wordmark } from "./Wordmark";

/**
 * Desktop-only left panel: the brand photo with a mode-aware overlay, the Skuey
 * wordmark + product tagline, and a testimonial. Constant across all auth pages.
 */
export function AuthBrandPanel() {
  return (
    <Panel>
      <Photo src={authBg} alt="" />
      <Inner>
        <Top>
          <Wordmark size={32} />
          <Copy>
            <Tagline>Inventory Management Simplified</Tagline>
            <Rule />
            <Desc>
              Track inventory, manage stock, and streamline operations across
              all your locations.
            </Desc>
          </Copy>
        </Top>
        <Testimonial />
      </Inner>
    </Panel>
  );
}

const Panel = styled("aside")(({ theme }) => ({
  position: "relative",
  flex: "0 0 44%",
  minWidth: 0,
  overflow: "hidden",
  backgroundColor: theme.skuey.surfaceAlt,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 600,
  // Gradient laid over the photo so the wordmark/copy stay readable.
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: theme.skuey.brandPanelOverlay,
  },
}));

const Photo = styled("img")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  // Lock the right edge (like mobile locks the bottom) — cropping eats the
  // left, keeping the visible right side of the photo fixed.
  objectPosition: "right center",
  display: "block",
});

const Inner = styled(Box)({
  position: "relative",
  zIndex: 1,
  height: "100%",
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: tokens.space(70),
  padding: tokens.space(10),
  alignItems: 'center',
  width: '400px'
});

const Top = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(6),
});

// Brand-accent rule under the tagline.
const Rule = styled(Divider)({
  width: "30%",
  borderColor: "#237afa",
  borderWidth: 1,
  borderRadius: 20,
});

const Copy = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(3),
  maxWidth: 420,
});

const Tagline = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontWeight: tokens.font.weight.bold,
  fontSize: tokens.font.size.display[0],
  lineHeight: `${tokens.font.size.display[1]}px`,
  color: theme.skuey.ink,
}));

const Desc = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  lineHeight: `${tokens.font.size.body[1]}px`,
  color: theme.skuey.muted,
  fontWeight: tokens.font.weight.medium,
}));
